/**
 * HelpMenu — compact Help dropdown for the app header.
 *
 * Five entries: User Guide (PDF), README, About QUOD-Finder,
 * Licence & Plan, Contact QUOD-Tech.
 *
 * Closes on outside click, Escape, or item selection. Keyboard
 * accessible (ArrowDown opens, Enter/Space select). Re-uses the
 * existing liquid-glass dropdown styling (`qf-glass-panel`,
 * `qf-spring-in`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const USER_GUIDE_PDF = '/docs/QUOD-Finder-User-Manual.pdf';

export default function HelpMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const onTriggerKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const items = [
    {
      key: 'user-guide',
      label: 'User Guide',
      hint: 'Operator handbook (PDF)',
      onSelect: () => {
        setOpen(false);
        // Open PDF in a new tab; never replaces the app.
        window.open(USER_GUIDE_PDF, '_blank', 'noopener,noreferrer');
      },
    },
    {
      key: 'readme',
      label: 'README',
      hint: 'Platform overview',
      onSelect: () => go('/help/readme'),
    },
    {
      key: 'about',
      label: 'About QUOD-Finder',
      hint: 'Version and operator',
      onSelect: () => go('/help/about'),
    },
    {
      key: 'licence',
      label: 'Licence & Plan',
      hint: 'Tenant tier and your access',
      onSelect: () => go('/help/licence'),
    },
    {
      key: 'contact',
      label: 'Contact QUOD-Tech',
      hint: 'Support and account questions',
      onSelect: () => go('/help/contact'),
    },
  ];

  return (
    <div className="app-header-help" data-testid="help-menu-root">
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-outline btn-sm app-header-help-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="app-header-help-menu"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        data-testid="help-menu-trigger"
      >
        Help
      </button>

      {open && (
        <div
          ref={panelRef}
          id="app-header-help-menu"
          role="menu"
          className="qf-glass-panel qf-spring-in app-header-help-panel"
          data-testid="help-menu-panel"
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className="app-header-help-item"
              onClick={it.onSelect}
              data-testid={`help-menu-${it.key}`}
            >
              <span className="app-header-help-label">{it.label}</span>
              <span className="app-header-help-hint">{it.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
